// Copyright 2017 Sorama B.V.

import { Disposable, autoinject, bindable, computedFrom, observable, transient } from "aurelia-framework";
import { AlertContent } from "controls/multi-alert/MultiAlert";
import { IAnimator } from "libraries/Export/IAnimator";
import { IExportable } from "libraries/Export/IExportable";
import { CustomizableAttributeTypes, SVGNode } from "libraries/SVG/SVGNode";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { ExportStage } from "services/Export/Export";
import { Format, FormatFeature, ImageFormat, VideoFormat } from "services/Export/Format";
import { FrameGenerator, IExportService } from "services/Export/IExportService";
import { UserSettingsService } from "services/UserManagement/UserSettingsService";
import { Phase } from "units/Phase";
import { Time } from "units/Time";
import { UnitValue } from "units/Unit";

interface ColorPreset {
    readonly name: string;
    readonly color: string;
}

@transient()
@autoinject
export class ImageExportModal implements IModal<ImageExportState>, IExportable {
    private static readonly userSettingsGroup: string = "ImageExportModal";
    private readonly minimumResolution: number = 64;

    private soramaLogoUri = "/resources/images/sorama_horizontal_all_white.png";

    private image: SVGNode;

    private disposables: Disposable[] = [];

    @observable
    private inputElement: HTMLElement;

    public resolutions = [
        { name: "Large", width: 1920, height: 1080 },
        { name: "Medium", width: 1280, height: 720 },
        { name: "Small", width: 640, height: 360 },
    ];

    public selectedResolution = this.resolutions[0]; // Default to Large

    private customizableAttributes: CustomAttributeViewModel[] = [];
    public colors: ColorPreset[] = [
        { name: "White", color: "white" },
        { name: "Black", color: "black" },
        { name: "Grey", color: "grey" },
        { name: "Red", color: "red" },
        { name: "Green", color: "green" },
        { name: "Blue", color: "blue" },
        { name: "Orange", color: "orange" },
        { name: "Yellow", color: "yellow" },
        { name: "Purple", color: "purple" },
        { name: "Cyan", color: "cyan" },
        { name: "Portal Blue", color: "#2c3e50" },
    ];

    private formats: Format[] = ImageFormat.all;
    private videoFormats: Format[] = VideoFormat.all;
    private showPreviewLegend = false;

    public exportStatus?: string;
    public exportProgress?: number;
    public imagePreview: string;

    protected logoPositions: string[] = LogoPositions.all;
    private fileName: string;

    private namedProperties: { name: string; value: string }[] = [];

    @userSetting() private background: string;

    @bindable @userSetting() private fileNamePreference = "";

    @observable
    @userSetting({
        toStringConverter: (format: Format) => format.name,
        fromStringConverter: (str: string) => ImageFormat.all.find((x) => x.name == str),
    })
    public fileFormat: Format = this.formats[0];

    @observable @userSetting() private selectedLogoPosition: string = LogoPositions.topRight;

    @observable private showLegendChecked: boolean;
    @observable private width: number = this.selectedResolution.width;
    @observable private height: number = this.selectedResolution.height;

    private svgWidth: number;
    private svgHeight: number;
    private svgOriginX: number;
    private svgOriginY: number;

    private animator: IAnimator;
    private exportable: IExportable;

    private measurementIds: string[];
    private canExportWithoutLogo = false;

    private imageUpdatePromise: Promise<void> = Promise.resolve();

    private error: string;

    private supportVideo = true;
    private exporting = false;
    private initializing = false;

    private toggles: { [index: number]: boolean } = {};

    private customAttributes: { [name: string]: string } = {};
    private attributeTypes: CustomizableAttributeTypes = new CustomizableAttributeTypes();
    private state: ImageExportState;
    private modalInstance: IWorkflowModuleModalInstance<ImageExportState>;

    @computedFrom("fileFormat")
    public get formatSupportsTransparency(): boolean {
        if (!this.fileFormat) return false;

        return this.fileFormat.supports(FormatFeature.Transparency);
    }

    @computedFrom("fileFormat")
    public get formatIsVideoFormat(): boolean {
        if (!this.fileFormat) return false;

        return this.fileFormat.supports(FormatFeature.Video);
    }

    @computedFrom("formatIsVideoFormat")
    public get maximumResolution(): number {
        return this.formatIsVideoFormat ? 1280 : 1920;
    }

    @computedFrom("state")
    public get isLegendDisplayed(): boolean {
        if (!this.state) return false;
        return this.state.isLegendDisplayed;
    }

    @computedFrom("error", "selectedLogoPosition")
    public get alerts(): AlertContent[] {
        const alerts: AlertContent[] = [];

        if (this.error) {
            alerts.push({
                alertText: "An error occurred",
                alertIcon: "fa-exclamation-triangle",
                alertSubText: this.error,
                alertType: "alert-danger",
                secondaryIcon: "",
            });
        }

        if (this.selectedLogoPosition === LogoPositions.None) {
            alerts.push({
                alertText: "No logo can costs Credits",
                alertIcon: "fa-exclamation-triangle",
                alertSubText:
                    "Exporting this image without Sorama logo costs 21 credits per measurement. One time only removal costs, valid for all export types.",
                alertType: "alert-warning",
                secondaryIcon: "",
            });
        }

        return alerts;
    }

    //#region Initialization

    constructor(
        private readonly exportService: IExportService,
        private readonly measurementRepository: MeasurementRepository,
        private readonly userSettingsService: UserSettingsService
    ) {}

    public activateModal(state: ImageExportState, modalInstance: IWorkflowModuleModalInstance<ImageExportState>) {
        if (state != undefined && modalInstance != undefined) {
            this.activateComponents(state, modalInstance);
        }
    }
    public activate(model?: ImageExportModalModel) {
        if (model != undefined) {
            this.activateComponents(model.state, model.moduleInstance);
        }
    }

    private async showLegendCheckedChanged(newValue: boolean): Promise<void> {
        if (this.state && typeof this.state.showLegend === "function") {
            this.state.showLegend(newValue);
            setTimeout(() => this.initializeImage(), 150);
        }
    }

    private async activateComponents(state, modalInstance): Promise<void> {
        this.initializing = true;
        this.modalInstance = modalInstance;

        this.state = state;
        this.customizableAttributes = [];
        this.measurementIds = state.measurementIds;
        this.showLegendChecked = this.state.initialLegendState;

        this.animator = state.animator;
        this.exportable = state.exportable;
        this.supportVideo = !!this.animator;

        this.formats = this.supportVideo ? ImageFormat.all.concat(VideoFormat.all) : ImageFormat.all;

        // Set file name and stuff
        this.fileName = state.initialFileName;
        this.namedProperties = state.namedProperties;

        if (LogoPositions.isLogoPosition(state.initialLogoPosition))
            this.selectedLogoPosition = state.initialLogoPosition;

        this.fileName += this.fileNamePreference;
        await this.initializeImage().then(() => {
            this.initializing = false;
        });
    }

    public deactivate(): ImageExportState {
        this.modalInstance.dispatch(() => {
            this.customizableAttributes = [];
            this.customAttributes = {};
            this.image = undefined;
            this.error = undefined;

            while (this.disposables.length > 0) this.disposables.pop().dispose();
        });

        return this.state;
    }

    private initializeImage(): Promise<void> {
        if (!this.modalInstance) return;

        return this.modalInstance.dispatch(() => {
            this.exportable
                .exportToSvg()
                .then((image) => {
                    // Clear the 'shape-rendering' attribute in the entire image
                    image.setAttribute("shape-rendering", undefined, true);
                    this.image = image;

                    // Determine aspect ratio of svg
                    const viewBox = this.image.attributes["viewBox"];
                    let splits: string[];

                    if (!viewBox || (splits = viewBox.split(",")).length != 4)
                        throw new Error("Invalid viewBox property on SVG, unable to render");

                    this.svgOriginX = Number(splits[0]);
                    this.svgOriginY = Number(splits[1]);
                    this.svgWidth = Number(splits[2]);
                    this.svgHeight = Number(splits[3]);

                    const promises: Promise<void>[] = [];

                    // Create a list of custom attributes
                    this.customizableAttributes = [];
                    const attributes = this.image.getAllCustomizableAttributes();
                    for (const key in attributes) {
                        if (!Object.prototype.hasOwnProperty.call(attributes, key)) continue;

                        const promise = this.userSettingsService
                            .get(ImageExportModal.userSettingsGroup, key)
                            .then((x) => {
                                this.customizableAttributes.push(
                                    new CustomAttributeViewModel(key, attributes[key].type, this.setAttribute, x)
                                );
                                this.customAttributes[key] = x;
                            })
                            .catch(() => {
                                this.customizableAttributes.push(
                                    new CustomAttributeViewModel(key, attributes[key].type, this.setAttribute)
                                );
                            });

                        promises.push(promise);
                    }

                    return Promise.all(promises).then(() => {
                        // Make sure customizable attributes are listed logically
                        this.customizableAttributes = this.customizableAttributes.sort((a, b) => {
                            if (a.name < b.name) return -1;
                            if (a.name > b.name) return 1;
                            return 0;
                        });

                        return this.generateLogo(this.image).then(() => {
                            // Re-render
                            this.renderPreview();
                        });
                    });
                })
                .catch((e) => {
                    if (e.message)
                        this.error = "Unable to generate image preview, the following error was provided: " + e.message;
                    else this.error = "Unable to generate image preview";
                });
        });
    }

    //#endregion

    //#region Change handlers

    public selectedLogoPositionChanged(): void {
        // Logo is embedded on the image, refresh the entire image
        this.initializeImage();
    }

    public onResolutionChange(): void {
        // Determine max width and height
        if (!this.svgWidth || !this.svgHeight) return;

        this.width = this.selectedResolution.width;
        this.height = this.selectedResolution.height;
    }

    public fileFormatChanged(): void {
        if (this.width > this.maximumResolution) this.width = this.maximumResolution;
        if (this.width < this.minimumResolution) this.width = this.minimumResolution;

        if (this.height > this.maximumResolution) this.height = this.maximumResolution;
        if (this.height < this.minimumResolution) this.height = this.minimumResolution;
    }

    //#endregion

    //#region User input handlers

    public export(): void {
        this.error = undefined;
        this.exporting = true;

        let exportPromise: Promise<void>;

        if (this.fileName.search(this.state.initialFileName) != -1) {
            this.fileNamePreference = this.fileName.split(this.state.initialFileName).pop();
        } else {
            const regex = /(\[.*?\])/; //[Property]
            const separatorCharacter = /[^a-zA-Z0-9]/; // To save first character before first [Property] in fileNamePreference
            const index = this.fileName.search(regex); //Find index of first [Property]

            this.fileNamePreference = separatorCharacter.test(this.fileName.substring(index - 1))
                ? this.fileName.slice(index - 1)
                : this.fileName.slice(index);

            if (this.fileNamePreference.search(regex) == -1)
                // Clean fileNamePreference when no [Property]'s are used
                this.fileNamePreference = "";
        }

        this.namedProperties.forEach(() => {
            //Add separator when user hasn't added a separator character to the filename
            this.fileName = this.fileName.replace("][", "]_[");
        });

        this.namedProperties.forEach((x) => {
            this.fileName = this.fileName.replace(x.name, x.value);

            //Remove duplicate properties from fileName
            while (this.fileName.indexOf(x.name) != -1) {
                this.fileName = this.fileName.replace(x.name, "");
            }
        });

        if (this.videoFormats.indexOf(this.fileFormat) != -1) {
            exportPromise = this.exportVideo();
        } else {
            exportPromise = this.exportImage();
        }

        let error: unknown = undefined;
        exportPromise
            .catch((e) => {
                if (e.message) this.error = "Export failed with the following message: " + e.message;
                else this.error = "Export failed";

                console.error(e);
                error = e;
            })
            .then(() => {
                this.exporting = false;
                this.modalInstance.lockModal(false);
                this.modalInstance.parentModule.lockModule(false);

                if (!error) {
                    this.modalInstance.close();
                }
            });
    }

    private exportImage(): Promise<void> {
        this.exportStatus = undefined;
        this.exportProgress = undefined;
        this.error = undefined;

        if (this.fileFormat.supports(FormatFeature.Video)) {
            this.error = "Unable to export image, the selected format is a video format";
        }

        return this.exportToSvg()
            .then((image) => {
                const svgImage = image.render(this.customAttributes);

                return this.exportService
                    .image(<ImageFormat>this.fileFormat, svgImage)
                    .fitResolution(this.width, this.height)
                    .export(this.fileName);
            })
            .catch((e) => {
                this.error = e.message ? e.message : "Unable to export image";
                throw e;
            });
    }

    private exportVideo(): Promise<void> {
        this.exportStatus = undefined;
        this.exportProgress = undefined;
        this.error = undefined;

        const generator = new AnimatorFrameGenerator(this.animator, this, this.customAttributes, (x) => {
            if (x.unit.isOfSameUnitFamily(Phase.degree)) {
                return new UnitValue(x.toUnit(Phase.degree) / 120, Time.second);
            }

            return x;
        });

        if (!this.fileFormat.supports(FormatFeature.Video)) {
            this.error = "Unable to export video, the selected format is not a video format";
        }

        return this.exportService
            .video(<VideoFormat>this.fileFormat, generator)
            .fitResolution(this.width, this.height)
            .setPlaybackRate(this.animator.playbackRate)
            .export(this.fileName, (stage, progress) => {
                switch (stage) {
                    case ExportStage.Generating:
                        this.exportStatus = "Generating Video..";
                        break;
                    case ExportStage.Encoding:
                        this.exportStatus = "Encoding Video..";
                        this.exportProgress = Math.min(progress * 100, 100);
                        break;
                }
            })
            .catch((e) => {
                this.error = e.message ? e.message : "Unable to export image";
                throw e;
            });
    }

    public cancel(): void {
        this.modalInstance.cancel();
    }

    //#region SVG generators

    public exportToSvg(): Promise<SVGNode> {
        // Determine if we need to give a resolution hint for higher res rendering.
        let scale: number = undefined;
        if (this.width && this.height) {
            scale = Math.min(this.width / this.svgWidth, this.height / this.svgHeight);
        } else if (this.width) {
            scale = Math.min(this.width / this.svgWidth, this.maximumResolution / this.svgHeight);
        } else if (this.height) {
            scale = Math.min(this.height / this.svgHeight, this.maximumResolution / this.svgWidth);
        }

        const suggestedWidth = scale ? this.svgWidth * scale : undefined;
        const suggestedHeight = scale ? this.svgHeight * scale : undefined;

        return this.exportable.exportToSvg(suggestedWidth, suggestedHeight).then((image) => {
            // remove width and height on main element
            image.removeAttribute("width");
            image.removeAttribute("height");

            // Set font for the entire SVG (can't use fancy fonts here unfortunately)
            image.setAttribute("font-family", "sans-serif");

            if (this.background) {
                const background = new SVGNode("rect");
                background.setAttribute("x", this.svgOriginX.toFixed(2));
                background.setAttribute("y", this.svgOriginY.toFixed(2));
                background.setAttribute("width", "100%");
                background.setAttribute("height", "100%");
                background.setAttribute("fill", this.background);

                image.addChild(background, 0);
            }

            // Render final image and save
            let logoPromise: Promise<void> = Promise.resolve();
            if (this.selectedLogoPosition.indexOf(LogoPositions.None) >= 0) {
                if (!this.canExportWithoutLogo) {
                    const promises: Promise<void>[] = [];

                    for (const measurementId of this.measurementIds) {
                        promises.push(this.measurementRepository.unlockNoLogoExport(measurementId));
                    }

                    logoPromise = Promise.all(promises).then(() => {
                        this.canExportWithoutLogo = true;
                    });
                }
            }

            return logoPromise.then(() => this.generateLogo(image));
        });
    }

    private generateLogo(image: SVGNode): Promise<SVGNode> {
        if (this.selectedLogoPosition.indexOf(LogoPositions.None) >= 0) return Promise.resolve(image);

        return new Promise<SVGNode>((resolve, reject) => {
            const imageEl = new Image();
            imageEl.src = this.soramaLogoUri;

            imageEl.onload = () => {
                // Draw the icon in the positive quadrant, for the best position (visibility)
                const svgWidth = this.svgWidth;
                const svgHeight = this.svgHeight;

                const margin = Math.min(svgWidth, svgHeight) * 0.12;

                const baseWidth = svgWidth * 0.15;
                const baseHeight = svgHeight * 0.15;
                const ratio = imageEl.width / imageEl.height;

                // ratio
                let width, height;

                if (baseWidth / ratio > baseHeight * ratio) {
                    // Height leading
                    height = baseHeight;
                    width = baseHeight * ratio;
                } else {
                    // Width leading
                    height = baseWidth / ratio;
                    width = baseWidth;
                }

                const x =
                    this.svgOriginX +
                    (this.selectedLogoPosition.indexOf("Left") !== -1 ? margin : svgWidth - width - margin);
                const y =
                    this.svgOriginY +
                    (this.selectedLogoPosition.indexOf("Top") !== -1 ? margin : svgHeight - height - margin);

                const canvas = document.createElement("canvas");
                canvas.width = imageEl.width;
                canvas.height = imageEl.height;

                canvas.getContext("2d").drawImage(imageEl, 0, 0);

                const logo = new SVGNode("image");
                logo.setAttribute("width", width.toString());
                logo.setAttribute("height", height.toString());
                logo.setAttribute("x", x.toString());
                logo.setAttribute("y", y.toString());
                logo.setAttribute("href", canvas.toDataURL("image/png"));

                image.addChild(logo);

                resolve(image);
            };

            imageEl.onerror = (e) => {
                reject(e);
            };
        });
    }

    private renderPreview = (): void => {
        if (!this.image) return;

        // Set image and element sizes
        this.image.removeAttribute("width");
        this.image.removeAttribute("height");

        this.imagePreview = this.image.render(this.customAttributes);
    };

    //#endregion

    //#region Attribute handlers

    public setAttribute = (attributeName: string, value: string): void => {
        if (value == undefined || value == null) {
            delete this.customAttributes[attributeName];

            // Update user settings
            this.userSettingsService.delete(ImageExportModal.userSettingsGroup, attributeName).catch(() => {});
        } else {
            this.customAttributes[attributeName] = value;

            // Update user settings
            this.userSettingsService.set(ImageExportModal.userSettingsGroup, attributeName, value).catch(() => {});
        }

        this.renderPreview();
    };

    //#endregion

    //#region FileNamePreference handlers
    public selectFileNameProperty(property: string): void {
        this.fileNamePreference = "";

        if (this.fileName.includes(property)) {
            this.fileName = this.fileName.replace(property, "");
        } else {
            this.fileNamePreference = property;
        }
    }

    public fileNamePreferenceChanged(): void {
        if (!this.exporting && this.fileNamePreference != "") this.fileName += this.fileNamePreference;

        this.inputElement.focus();
    }
    //#endregion
}

class CustomAttributeViewModel {
    public readonly name: string;
    public readonly type: string;

    // This flag is to prevent the valuechanged from firing from the constructor
    private constructed = false;

    @observable
    public value: string;

    private onPropertyChanged: (name: string, value: string) => void;

    constructor(name: string, type: string, onPropertyChanged: (name: string, value: string) => void, value?: string) {
        this.name = name;
        this.type = type;
        this.value = value;
        this.onPropertyChanged = onPropertyChanged;
        this.constructed = true;
    }

    private valueChanged(): void {
        if (!this.constructed) return;

        this.onPropertyChanged(this.name, this.value === "" ? undefined : this.value);
    }
}

// View-Model for modal needs to be first class in the file
export class ImageExportState {
    public measurementIds: string[];

    public initialFileName: string;

    public initialLogoPosition?: string;

    public isLegendDisplayed: boolean;

    public initialLegendState?: boolean;

    public showLegend?: (key: boolean) => boolean;

    public timeBaseConverter?: (key: UnitValue) => UnitValue;

    public exportable: IExportable;

    public animator?: IAnimator;

    public namedProperties?: NamedProperty[];
}

export class ImageExportModalModel {
    public state: ImageExportState;
    public moduleInstance: IWorkflowModuleInstance;
}

export class NamedProperty {
    name: string;
    value: string;
}

export class LogoPositions {
    public static None = "None";
    public static topLeft = "Top Left";
    public static topRight = "Top Right";
    public static bottomLeft = "Bottom Left";
    public static bottomRight = "Bottom Right";

    public static all: string[] = [
        LogoPositions.topLeft,
        LogoPositions.topRight,
        LogoPositions.bottomLeft,
        LogoPositions.bottomRight,
        LogoPositions.None,
    ];

    public static isLogoPosition(logoPosition: string): boolean {
        return LogoPositions.all.some((x) => x === logoPosition);
    }
}

class AnimatorFrameGenerator implements FrameGenerator {
    private _finalFrame: boolean;
    public get finalFrame(): boolean {
        return this._finalFrame;
    }

    public get frameTime(): UnitValue {
        return this.unitConverter(this.animator.currentFrameTime);
    }

    private animator: IAnimator;
    private exportable: IExportable;
    private attributes: { [name: string]: string };
    private unitConverter: (v: UnitValue) => UnitValue;
    private startPromise: Promise<void>;

    constructor(
        animator: IAnimator,
        exportable: IExportable,
        attributes: { [name: string]: string },
        unitConverter: (v: UnitValue) => UnitValue
    ) {
        this.animator = animator;
        this.exportable = exportable;
        this.attributes = attributes;
        this.unitConverter = unitConverter;

        this.startPromise = this.animator.startAnimation();
    }

    public getFrame(): Promise<string> {
        return this.startPromise.then(() => {
            return this.exportable.exportToSvg().then((svgNode) => {
                const svgImage = svgNode.render(this.attributes);

                return this.animator.nextAnimationStep().then((x) => {
                    this._finalFrame = !x;

                    return svgImage;
                });
            });
        });
    }
}
